import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { deleteParentCookie, setParentCookie } from './common/Util';
import config from './common/config';
import i18n from './common/i18n';
import createLogger from './common/logger';
import { setCustomerToken, setSocketioClient, setSocketioClientReady } from './features/socketioSlice';
import { clearPendingSocketEmitQueue } from './common/socketEmitQueue';
import LivechatService from './services/LivechatService';

const logger = createLogger('socket');

export default function SocketioLayer() {
	const dispatch = useDispatch();

	const [isErrorDomain, setIsErrorDomain] = useState(false);

	const orgId = useSelector((store: any) => store.socketio.orgId);
	const client = useSelector((store: any) => store.socketio.client);
	const customerToken = useSelector((store: any) => store.socketio.customerToken);

	const resetSocketSession = (socketClient: any, reason: string, shouldRegenerateIdentity = true) => {
		logger.warn('reset session', {
			reason,
			shouldRegenerateIdentity,
			hasClient: Boolean(socketClient),
			customerToken,
		});

		clearPendingSocketEmitQueue();
		deleteParentCookie(config.LOCAL_STORAGE_CUSTOMER_TOKEN);

		if (socketClient) {
			socketClient.off();
			socketClient.io.off();
			socketClient.disconnect();
		}

		dispatch(setSocketioClientReady(false));
		dispatch(setSocketioClient(null));

		if (shouldRegenerateIdentity) {
			dispatch(setCustomerToken(''));
		}
	};

	const generateToken = async () => {
		const response = await LivechatService.generateToken(orgId);
		if (!response?.customerToken) {
			// TODO: handle failed case
			logger.warn('generateToken returned empty customerToken', { orgId });
		}
		dispatch(setCustomerToken(response.customerToken));
	};

	const initClient = () => {
		const socketClient: any = io(config.SOCKET_SERVER_URL, {
			forceNew: true,
			path: '/livechat/',
			auth: { customerToken, encryptedOrgId: orgId },
			query: { locale: i18n.language },
			transports: ['websocket'],
			autoConnect: false,
		});

		logger.log('init client', {
			orgId,
			customerToken,
			locale: i18n.language,
			url: config.SOCKET_SERVER_URL,
		});

		socketClient.on('connect', () => {
			logger.log('transport connected', {
				id: socketClient.id,
				connected: socketClient.connected,
			});
		});

		socketClient.on('ready', (token: any) => {
			socketClient.token = token;
			logger.log('ready', {
				token,
				id: socketClient.id,
				connected: socketClient.connected,
			});

			dispatch(setCustomerToken(token));
			setParentCookie(config.LOCAL_STORAGE_CUSTOMER_TOKEN, token);
			dispatch(setSocketioClientReady(true));

			// 通知外層已經建立好連結，代表livechat OK
			// customerToken 一併回拋，讓宿主頁（如 orra）能同步 customer info
			window.parent.postMessage(
				{
					target: 'eightChat',
					command: 'connectReady',
					options: { status: true, customerToken: token },
				},
				'*'
			);
		});
		socketClient.on('disconnect', (reason: any) => {
			logger.log('disconnected', {
				reason,
				id: socketClient.id,
				connected: socketClient.connected,
			});
			dispatch(setSocketioClientReady(false));

			// 通知外層已經建立好連結，代表livechat OK
			window.parent.postMessage(
				{
					target: 'eightChat',
					command: 'connectReady',
					options: { status: false },
				},
				'*'
			);
		});

		socketClient.io.on('reconnect_attempt', (attempt: number) => {
			logger.log('reconnect attempt', {
				attempt,
				connected: socketClient.connected,
			});
		});

		socketClient.io.on('reconnect', (attempt: number) => {
			logger.log('reconnected', {
				attempt,
				id: socketClient.id,
				connected: socketClient.connected,
			});
		});

		socketClient.on('connect_error', (error: any) => {
			logger.error('connect_error', {
				message: error?.message,
				description: error?.description,
				context: error?.context,
				type: error?.type,
			});
		});

		socketClient.on('errorReceived', (data: any) => {
			logger.warn('errorReceived', data);
			if (data.code === 'live.chat.domainNotAccept') {
				setIsErrorDomain(true);
				resetSocketSession(socketClient, data.code, false);
				return;
			}

			resetSocketSession(socketClient, data.code || 'errorReceived');
		});

		logger.log('connect() called');
		socketClient.connect();
		dispatch(setSocketioClient(socketClient));
	};

	useEffect(() => {
		if (!client && customerToken && orgId && !isErrorDomain) {
			initClient();
		}

		return () => {
			if (client) {
				logger.log('cleanup client listeners', {
					id: client.id,
					connected: client.connected,
				});
				client.off();
				client.io.off();
				dispatch(setSocketioClient(null));
				dispatch(setSocketioClientReady(false));
			}
		};
	}, [client, orgId, isErrorDomain, customerToken]);

	useEffect(() => {
		if (!customerToken && orgId) {
			generateToken();
		}
	}, [orgId, customerToken]);

	return null;
}
